import React, { useContext, useEffect, useRef } from 'react';
import { cn } from '../../../lib/utils';
import GlobalContext from '../../utils/GlobalContext';

export default function TextAreaInput({
  delay = 0,
  disabled = false,
  value = '',
  placeholder,
  onTextareaChange = () => {},
  className = '',
  ...props
}) {
  const { locales } = useContext(GlobalContext);
  const defaultPlaceholder = placeholder || locales.common.textareaPlaceholder;

  const textareaRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleResize = () => {
    if (textareaRef.current) {
      if (textareaRef.current && textareaRef.current.offsetParent !== null) {
        textareaRef.current.style.height = 'inherit';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }
  };

  useEffect(() => {
    handleResize();
  }, [value, defaultPlaceholder]);

  useEffect(() => {
    // Create an observer when the component mounts that listens for intersection with the component and the viewport (defining when the component can be actually seen on the screen rather than just mounted)
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleResize();
          if (textareaRef.current) {
            observer.unobserve(textareaRef.current); // un-observing here to avoid multiple height resizing which will cause unwanted behaviour on page scroll
          }
        }
      },
      { root: null, threshold: 0 },
    );

    // Start observing the textarea
    if (textareaRef.current) {
      observer.observe(textareaRef.current);
    }

    return () => {
      observer.disconnect(); // catches all edge cases where multiple observations might still be active
    };
  }, []);

  return (
    <textarea
      ref={textareaRef}
      className={cn(
        'box-border p-2.5 w-full text-gray-700 bg-gray-50 rounded-lg border border-white focus:ring-blue-500 focus:border-blue-500 min-h-[40px] overflow-y-auto resize-none',
        className,
      )}
      placeholder={defaultPlaceholder}
      value={value}
      onInput={() => {
        handleResize();

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        if (delay > 0) {
          timeoutRef.current = setTimeout(() => {
            onTextareaChange();
          }, delay);
        } else {
          onTextareaChange();
        }
      }}
      data-hj-allow
      disabled={disabled}
      {...props}
    />
  );
}
